import { Button, Input, Modal, ProfilePhoto, Text } from "@minervaproject/fds";
import clsx from "clsx";
import { useCallback, useState } from "react";
import { ConnectDragSource, useDrag, useDrop } from "react-dnd";

import { ParticipantTileContent } from "components/shared/participant_tile_content";
import { useUserIdsWithCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { collect, useDragHandler } from "hooks/use_drag_handler";
import { useUserProperties } from "hooks/use_user_properties";
import { moveBreakoutRoomUser } from "shared/reducers/classroom";
import { BreakoutRoomType, CapabilityType } from "shared/types";
import { UserType } from "shared/types/user";
import { classroomSelectors, localSelectors } from "store";
import { computeInitials, getOrderForRole, longDisplayName, profilePhotoUrl } from "utils/user";

export const BREAKOUT_ROOM_USER_DRAG_TYPE = "BREAKOUT_ROOM_USER_DRAG_TYPE";

function MoveBreakoutUserModal({
  breakoutRoom,
  user,
  totalNumberOfBreakoutRooms,
  handleClose,
}: {
  breakoutRoom: BreakoutRoomType;
  user: UserType;
  totalNumberOfBreakoutRooms: number;
  handleClose: () => void;
}) {
  const dispatch = useAppDispatch();
  const [newBreakoutRoomTitle, setNewBreakoutRoomTitle] = useState("");

  const isNewBreakoutRoomTitleValid = () => {
    const newBreakoutRoomNumber = parseInt(newBreakoutRoomTitle, 10);
    return (
      newBreakoutRoomTitle !== breakoutRoom.title &&
      newBreakoutRoomTitle.indexOf(".") === -1 &&
      newBreakoutRoomNumber > 0 &&
      newBreakoutRoomNumber <= totalNumberOfBreakoutRooms
    );
  };

  const handleMoveBreakoutUser = () => {
    dispatch(
      moveBreakoutRoomUser({
        userId: user.id,
        oldBreakoutRoomId: breakoutRoom.id,
        newBreakoutRoomTitle: newBreakoutRoomTitle.trim(),
      }),
    );
  };

  return (
    <Modal
      title="Move Participant"
      size="sm"
      onClose={handleClose}
      data-testid="move-breakout-user-modal"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <div className="flex items-center justify-between">
          <div>
            <Text className="mb2" kind="body-s" color="blackTint40" bold>
              Participant
            </Text>
            <div className="flex items-center">
              <ProfilePhoto
                className="mr2"
                size="md"
                src={profilePhotoUrl(user)}
                initials={computeInitials(user)}
                style={{ opacity: user.is_present ? 1 : 0.5 }}
              />
              <Text>
                {longDisplayName(user)} (in Group {breakoutRoom.title})
              </Text>
            </div>
          </div>
          <div>
            <Text className="mb2" kind="body-s" color="blackTint40" bold>
              New Group #
            </Text>
            <Input
              autoFocus
              aria-label="new-breakout-room-title"
              onChange={(title: string) => setNewBreakoutRoomTitle(title)}
              value={newBreakoutRoomTitle}
              size="xs"
              type="number"
              min={1}
              max={totalNumberOfBreakoutRooms}
            />
          </div>
        </div>
        <Button
          type="submit"
          disabled={!isNewBreakoutRoomTitleValid()}
          mt={5}
          onClick={handleMoveBreakoutUser}
        >
          Submit
        </Button>
      </form>
    </Modal>
  );
}

function BreakoutRoomUserAv({
  userId,
  isPresent,
  dragSource,
  includeVideo,
  index,
}: {
  userId: string;
  isPresent: boolean;
  dragSource: ConnectDragSource;
  includeVideo: boolean;
  index: number;
}) {
  const canControlClassroomUserIds = useUserIdsWithCapability(CapabilityType.CAN_CONTROL_CLASSROOM);
  const canControlClassroom = canControlClassroomUserIds.includes(userId);
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const currentUserCanControlClassroom = canControlClassroomUserIds.includes(currentUserId);

  return (
    <div
      className={clsx(
        "participant-container bg-black-tint-10 relative outline-2-black-tint-10 cursor-pointer",
        {
          "participant-minimal-no-video-not-present": !includeVideo && !isPresent,
        },
      )}
      style={{ height: "100%", width: "100%" }}
      ref={dragSource}
      data-testid={`participant-${userId}`}
    >
      <ParticipantTileContent
        userId={userId}
        canControlClassroom={canControlClassroom}
        currentUserCanControlClassroom={currentUserCanControlClassroom}
        isSelf={false}
        profilePhotoSize="md"
        signalSize="md"
        includeVideo={includeVideo}
        isMinimal={!includeVideo}
        index={index}
        noHover
      />
    </div>
  );
}

function BreakoutRoomUser({
  breakoutRoom,
  user,
  totalNumberOfBreakoutRooms,
  hasAv,
  hideVideo,
  index,
}: {
  breakoutRoom: BreakoutRoomType;
  user: UserType;
  totalNumberOfBreakoutRooms: number;
  hasAv?: boolean;
  hideVideo?: boolean;
  index: number;
}) {
  const [showMoveBreakoutUserModal, setShowMoveBreakoutUserModal] = useState(false);
  const listeningBreakoutRoom = useAppSelector(
    classroomSelectors.selectListeningBreakoutRoomForCurrentUser(),
  );
  const isSpeaking = useUserProperties(user.id, ["is_speaking"])?.is_speaking;
  let useAvComponent = hasAv && !hideVideo;
  // Videos hidden will not have an A/V component - we won't subscribe to them unless we are listening to the breakout
  if (!!listeningBreakoutRoom && hideVideo) {
    useAvComponent =
      listeningBreakoutRoom.assigned_user_ids.includes(`${user.id}`) ||
      listeningBreakoutRoom.visiting_user_ids.includes(`${user.id}`);
  }

  const [{ isDragging }, drag] = useDrag({
    type: BREAKOUT_ROOM_USER_DRAG_TYPE,
    item: {
      userId: user.id,
      oldBreakoutRoomId: breakoutRoom.id,
    },
    collect,
  });
  useDragHandler(isDragging);

  const handleClick = useCallback(() => {
    if (hideVideo || !hasAv) setShowMoveBreakoutUserModal(true);
  }, [hideVideo, hasAv]);

  const styles: React.CSSProperties = {};
  if (useAvComponent) {
    styles.aspectRatio = "1 / 1";
    styles.boxShadow = "0px 1px 12px 0px rgba(1, 1, 1, 0.30), 0px 3px 4px 0px rgba(1, 1, 1, 0.40)";

    if (hideVideo) {
      styles.width = "32px";
    } else {
      styles.maxWidth = "86px";
      styles.minWidth = "45px";
      styles.width = "15%";
    }
  }

  return (
    <div
      className="flex items-center mr2"
      data-testid={`breakout-room-user-${user.id}`}
      onClick={handleClick}
      style={styles}
    >
      {useAvComponent ? (
        <BreakoutRoomUserAv
          userId={user.id}
          isPresent={user.is_present}
          dragSource={drag}
          includeVideo={!hideVideo}
          index={index}
        />
      ) : (
        <ProfilePhoto
          className={clsx("outline-2-black-tint-10 mr2 cursor-pointer", {
            "is-speaking": isSpeaking,
          })}
          size="md"
          src={profilePhotoUrl(user)}
          initials={computeInitials(user)}
          style={{ opacity: user.is_present ? 1 : 0.5 }}
          ref={drag}
        />
      )}
      {showMoveBreakoutUserModal && (
        <MoveBreakoutUserModal
          breakoutRoom={breakoutRoom}
          user={user}
          totalNumberOfBreakoutRooms={totalNumberOfBreakoutRooms}
          handleClose={() => setShowMoveBreakoutUserModal(false)}
        />
      )}
    </div>
  );
}

// hasAv is true if the class is virtual
// hideVideo is false for the preview view and true for the list view
function BreakoutRoomUsers({
  breakoutRoom,
  users,
  totalNumberOfBreakoutRooms,
  hasAv,
  hideVideo,
}: {
  breakoutRoom: BreakoutRoomType;
  users: UserType[];
  totalNumberOfBreakoutRooms: number;
  hasAv?: boolean;
  hideVideo?: boolean;
}) {
  const isInPerson = useAppSelector(classroomSelectors.selectIsInPersonClass());
  const dispatch = useAppDispatch();
  const [, drop] = useDrop({
    accept: [BREAKOUT_ROOM_USER_DRAG_TYPE],
    drop: (item: { userId: string; oldBreakoutRoomId: string }) => {
      if (item.oldBreakoutRoomId !== breakoutRoom.id) {
        dispatch(
          moveBreakoutRoomUser({
            userId: item.userId,
            oldBreakoutRoomId: item.oldBreakoutRoomId,
            newBreakoutRoomId: breakoutRoom.id,
          }),
        );
      }
    },
  });
  const presentUsers = users
    .filter((user) => user.is_present)
    .sort((a, b) => getOrderForRole(a) - getOrderForRole(b));
  const notPresentUsers = users.filter((user) => !user.is_present);
  let sortedUsers = presentUsers;
  // Do not show absent users in virtual classes
  if (isInPerson) {
    sortedUsers = sortedUsers.concat(notPresentUsers);
  }

  return (
    <div
      className="flex p2"
      style={{
        minHeight: hasAv && !hideVideo ? (sortedUsers.length ? "45px" : "0px") : "32px",
        overflowX: "auto",
      }}
      ref={drop}
      data-testid={`breakout-room-users-${breakoutRoom.id}`}
    >
      {sortedUsers.map((user, index) => (
        <BreakoutRoomUser
          key={user.id}
          breakoutRoom={breakoutRoom}
          user={user}
          totalNumberOfBreakoutRooms={totalNumberOfBreakoutRooms}
          hasAv={hasAv}
          hideVideo={hideVideo}
          index={index}
        />
      ))}
    </div>
  );
}

export { BreakoutRoomUser, BreakoutRoomUserAv, BreakoutRoomUsers, MoveBreakoutUserModal };
